// ═══════════════════════════════════════════════════════════════════
//  TOQUM · DOCUMENT AI EXTRACTION
//  Copied verbatim from Nirvikalp StyleRegistry.js so that the
//  extraction process AND the resulting data shapes are identical.
//  Endpoint: {API_URL}/ai_service.php  ·  docType: PO | TECHPACK | SPEC
// ═══════════════════════════════════════════════════════════════════
import { API_URL } from './Shared';

export const AI_BASE = 'https://shayana.pythonanywhere.com';

// Images are downscaled to max 1200px and sent as JPEG data URLs.
// Text/CSV is sent raw. PDF/Excel must be screenshotted or saved as CSV.
export const processFile = (file) => new Promise((resolve, reject) => {
  if (file.type.match(/image.*/)) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        const MAX = 1200;
        if (w > h) { if (w > MAX) { h *= MAX / w; w = MAX; } }
        else       { if (h > MAX) { w *= MAX / h; h = MAX; } }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve({ data: canvas.toDataURL('image/jpeg', 0.7), type:'image' });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  } else if (file.type.match(/text.*/) || file.name.endsWith('.csv')) {
    const reader = new FileReader();
    reader.onload = (e) => resolve({ data: e.target.result, type:'text' });
    reader.readAsText(file);
  } else {
    alert('For PDF/Excel, please upload a SCREENSHOT (image) or save as CSV.');
    reject(new Error('Unsupported file type'));
  }
});

export const extractDocumentData = async (file, type) => {
  const processed = await processFile(file);
  const response = await fetch(`${API_URL}/ai_service.php`, {
    method:'POST', headers:{ 'Content-Type':'application/json' },
    body: JSON.stringify({ file: processed.data, fileType: processed.type, docType: type }),
  });
  const text = await response.text();
  let data;
  try { data = JSON.parse(text); }
  catch (e) { throw new Error('Server Error: ' + text.substring(0,100)); }
  if (data.error) throw new Error(data.error);
  return data;
};

export const fileToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

// ── PO extraction → normalised PO lines (StyleRegistry logic) ────
export const mapExtractedPOs = (data, base64, fileName) => {
  const poData = data.po_details || [];
  const hasPerSizeRows = poData.length > 0 &&
    poData.some(po => (po.size || po.Size) && !(po.size_breakdown || po.sizeBreakdown));

  if (hasPerSizeRows) {
    const grouped = {};
    const topPoNo = data.po_no || data.po_number || data['PO#'] || '';
    const topDate = data.ex_fty_date || data.po_date || '';
    const topDest = data.destination || '';
    poData.forEach(po => {
      const poNo  = po.po_no || po.po_number || po['PO#'] || po['PO'] || topPoNo || '-';
      const color = po.color || po.colour || po.Color || po.Colour || 'Mix';
      const key = `${poNo}||${color}`;
      if (!grouped[key]) grouped[key] = { po_no:poNo, color,
        date: po.ex_fty_date || po.po_date || topDate || '',
        fob: po.base_price || po.fob_price || po.fob || data.fob || '',
        dest: po.destination || topDest || '', sizes:[] };
      const sz  = (po.size || po.Size || '').toString().trim();
      const qty = parseFloat(po.qty || po.quantity || po.Qty || 0);
      if (sz && qty > 0) grouped[key].sizes.push({ size:sz, qty });
    });
    return Object.values(grouped).map((g, idx) => ({
      id: Date.now()+idx, num:g.po_no, col:g.color,
      qty: g.sizes.reduce((a,s)=>a+s.qty,0), date:g.date, fob:String(g.fob), dest:g.dest,
      sizeBreakdown: g.sizes.map(s=>`${s.size}:${s.qty}`).join(','),
      file:{ url:base64, name:fileName },
    }));
  }
  return poData.map((po, idx) => ({
    id: Date.now()+idx,
    num: po.po_no || po.po_number || po['PO#'] || po['PO'] || '-',
    col: po.color || po.colour || po.Color || 'Mix',
    qty: parseFloat(po.qty || po.po_qty || po.total_qty || 0),
    date: po.ex_fty_date || '', fob: po.fob_price || po.base_price || po.fob || '',
    dest: po.destination || '',
    sizeBreakdown: po.size_breakdown || po.sizeBreakdown || '',
    file:{ url:base64, name:fileName },
  }));
};

// ── Techpack extraction → BOM rows (StyleRegistry mapping) ───────
export const mapExtractedBOM = (data) => (data.bom || []).map(b => ({
  category:        b.category || b.section || 'General',
  item_name:       b.item_name || b.item || b.name || b.code || b.package_code || '',
  description:     b.description || b.material || b.spec || b.details || b.remarks || '',
  supplier:        b.supplier || b.vendor || '',
  reference:       b.reference || b.ref || b.item_code || b.mill_ref || '',
  consumption:     b.consumption || b.total_qty || '',
  placement:       b.placement || b.location || '',
  color_breakdown: b.color_breakdown || b.color || b.colour || '',
  size_variation:  b.size_variation || b.size_breakdown || '',
  comments:        b.comments || b.notes || b.remarks || '',
}));

// Merge BOM pages without duplicating (item_name + category key)
export const mergeBOM = (existing = [], incoming = []) => {
  const merged = [...existing];
  incoming.forEach(item => {
    const dupe = merged.some(e =>
      (e.item_name||'').toLowerCase() === (item.item_name||'').toLowerCase() &&
      (e.category ||'').toLowerCase() === (item.category ||'').toLowerCase());
    if (!dupe) merged.push(item);
  });
  return merged;
};

// ── PO colour/size matrix from extracted PO lines ────────────────
// Produces the { color, size, country, qty, shipDate } rows Toqum uses.
export const poLinesToMatrix = (poLines = []) => {
  const rows = [];
  poLines.forEach(po => {
    const sb = po.sizeBreakdown;
    if (typeof sb === 'string' && sb.trim()) {
      sb.split(',').forEach(part => {
        const [size, qty] = part.split(':').map(s=>s.trim());
        if (size) rows.push({ color:po.col||'All', size, country:po.dest||'',
          qty: parseInt(qty,10)||0, shipDate: po.date||'' });
      });
    } else if (sb && typeof sb === 'object' && !Array.isArray(sb)) {
      Object.entries(sb).forEach(([size,qty]) => rows.push({ color:po.col||'All', size,
        country:po.dest||'', qty: parseInt(qty,10)||0, shipDate: po.date||'' }));
    } else if (Array.isArray(sb)) {
      sb.forEach(it => rows.push({ color: it.color||po.col||'All', size: it.size||'ALL',
        country:po.dest||'', qty: parseInt(it.qty,10)||0, shipDate: po.date||'' }));
    } else {
      rows.push({ color:po.col||'All', size:'ALL', country:po.dest||'',
        qty: parseInt(po.qty,10)||0, shipDate: po.date||'' });
    }
  });
  return rows;
};

// Spec sheet extraction — map AI output to StyleModule measurements format.
// AI returns: { style_name, headers:['POM','TOL +/-','S','M','L',...], rows:[{pom, tolerance, S, M, ...}] }
// StyleModule expects: { sizes:['S','M','L',...], rows:[{pom, tol_minus, tol_plus, S, M, ...}] }
export const mapExtractedSpec = (data) => {
  if (!data) return data;

  // Already in correct format
  if (data.sizes && Array.isArray(data.sizes) && data.rows?.length) return data;

  // Extract sizes from headers — exclude non-size columns
  const NON_SIZE = ['pom','point','description','measurement','tol','tol +/-','tol+/-',
    'tolerance','tol -','tol +','tol+','tol-','s/n','#',''];
  const headers = data.headers || [];
  const sizes = headers.filter(h => !NON_SIZE.includes((h||'').toLowerCase().trim()));

  const rows = (data.rows || []).map(r => {
    // Parse tolerance — could be '±0.5', '0.50', '+/- 0.5' etc.
    const tolRaw = r.tolerance || r['TOL +/-'] || r['tol'] || '';
    const tolNum = parseFloat(String(tolRaw).replace(/[±+\-\s]/g,'')) || 0;
    const tol_minus = r.tol_minus || r.tolMinus || tolNum || '';
    const tol_plus  = r.tol_plus  || r.tolPlus  || tolNum || '';

    const mapped = {
      pom:      r.pom || r.point || r.description || r.measurement || '',
      tol_minus: String(tol_minus),
      tol_plus:  String(tol_plus),
      tolerance: String(tol_plus || tol_minus || ''),
    };
    // Copy size values
    sizes.forEach(s => { mapped[s] = r[s] ?? ''; });
    return mapped;
  }).filter(r => r.pom);

  return {
    sizes,
    rows,
    style_name: data.style_name || data.styleName || '',
    brand:      data.brand || '',
    fit:        data.fit || '',
    gender:     data.gender || '',
    vendor:     data.vendor || '',
  };
};
